import { Point, Line, Polygon } from '../../../context/context';
import { processKMLContent } from '../processors/kml';
import { hasRequiredGtfsCoreFiles } from '../../../utils/fixedRoutes/gtfsZipUtils';
import { emitGtfsFileDropped, emitNetworkFeedDropped } from './gtfsImportEvent';

interface DroppedFile {
  file: File;
  type: 'excel' | 'gtfs' | 'kml';
}

interface DragDropHandlerProps {
  setIsProcessingGTFS: (isProcessing: boolean) => void;
  setGtfsError: (error: string | null) => void;
  setDroppedFile: (file: DroppedFile | null) => void;
  setIsImportTripsModalOpen: (isOpen: boolean) => void;
  setIsDropActive: (isActive: boolean) => void;
  handleImportTrips: (points: Point[], lines: Line[]) => void;
  handleImportKMLShapes: (shapes: (Point | Line | Polygon)[]) => void;
}

const isZipFileName = (name: string): boolean => name.toLowerCase().endsWith('.zip');

async function isGtfsZip(file: File): Promise<boolean> {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  const zipContent = await zip.loadAsync(file);
  return hasRequiredGtfsCoreFiles(zipContent);
}

/** A zip that holds `.xml`/nested `.zip` entries (no GTFS `.txt`) — i.e. a TransXChange dataset. */
async function isTransXChangeZip(file: File): Promise<boolean> {
  const JSZip = (await import('jszip')).default;
  const zip = await new JSZip().loadAsync(file);
  const names = Object.keys(zip.files);
  if (names.some((n) => n.toLowerCase().endsWith('.txt'))) return false; // looks like GTFS
  return names.some((n) => /\.(xml|zip)$/i.test(n));
}

export function useDragDropHandlers({
  setIsProcessingGTFS,
  setGtfsError,
  setDroppedFile,
  setIsImportTripsModalOpen,
  setIsDropActive,
  handleImportTrips: _handleImportTrips,
  handleImportKMLShapes,
}: DragDropHandlerProps) {
  const processGTFSFile = async (file: File) => {
    setIsProcessingGTFS(true);
    setGtfsError(null);
    try {
      if (!isZipFileName(file.name)) {
        throw new Error('Please provide a GTFS zip file.');
      }
      const isGtfs = await isGtfsZip(file);
      if (!isGtfs) {
        throw new Error('ZIP does not contain required GTFS files (stops, routes, trips, stop_times).');
      }
      emitGtfsFileDropped(file);
      setDroppedFile(null);
    } catch (err) {
      setGtfsError('Error processing GTFS file: ' + (err as Error).message);
    } finally {
      setIsProcessingGTFS(false);
    }
  };

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const ext = file.name.toLowerCase().split('.').pop();

      if (ext === 'zip') {
        await processGTFSFile(file);
        setIsDropActive(false);
        return;
      }

      if (ext === 'xlsx' || ext === 'xls') {
        setDroppedFile({ file, type: 'excel' });
        setIsImportTripsModalOpen(true);
      } else if (ext === 'kml') {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const text = e.target?.result as string;
            const shapes = processKMLContent(text);
            if (shapes.length > 0) {
              handleImportKMLShapes(shapes);
            }
          } catch (err) {
            console.error('Error processing KML file:', err);
          }
        };
        reader.readAsText(file);
      }
    }
    setIsDropActive(false);
  };

  return {
    onDrop,
    processGTFSFile,
  };
}

/**
 * Handles file drop for the map, supporting GTFS, Excel/CSV, and KML files.
 */
export async function onDropMapFile(
  acceptedFiles: File[],
  global: any,
  setImportedFile: (file: File) => void,
  setImportModalOpen: (open: boolean) => void,
  handleImportedShapes: (global: any, shapes: any[]) => void,
  setIsDropActive: (active: boolean) => void,
) {
  if (acceptedFiles.length === 0) {
    setIsDropActive(false);
    return;
  }

  const file = acceptedFiles[0];

  try {
    if (isZipFileName(file.name)) {
      // Only introspect SMALL zips in the browser — JSZip.loadAsync reads the whole file,
      // which freezes/OOMs on a national feed (London ≈ 314 MB). A small GTFS zip imports
      // into the Fixed Routes drawer (existing flow); everything else (TransXChange, or any
      // large zip) goes to the network store, which sniffs the format server-side (PRD-05b).
      const INTROSPECT_CAP = 60 * 1024 * 1024; // 60 MB
      let routed = false;
      if (file.size <= INTROSPECT_CAP) {
        try {
          if (await isGtfsZip(file)) {
            console.log('[drop] GTFS zip → Fixed Routes import:', file.name);
            emitGtfsFileDropped(file);
            routed = true;
          } else if (await isTransXChangeZip(file)) {
            console.log('[drop] TransXChange zip → network store ingest:', file.name);
            emitNetworkFeedDropped(file);
            routed = true;
          }
        } catch (error) {
          console.error('[drop] error checking dropped zip (will route to store):', error);
        }
      }
      if (!routed) {
        console.log('[drop] large/unclassified zip → network store ingest:', file.name, file.size);
        emitNetworkFeedDropped(file);
      }
      return;
    }

    // A bare TransXChange XML → network-store ingest (the backend sniffs + converts).
    if (file.name.toLowerCase().endsWith('.xml')) {
      console.log('[drop] xml → network store ingest:', file.name);
      emitNetworkFeedDropped(file);
      return;
    }

    if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv')) {
      setImportedFile(file);
      setImportModalOpen(true);
      return;
    }

    if (file.name.endsWith('.kml')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          import('../../layer_panel/ImportLayerModal').then((mod) => {
            if (mod.parseKMLShapes) {
              const shapes = mod.parseKMLShapes(text);
              handleImportedShapes(global, shapes);
            }
          });
        } catch (err) {
          console.error('Error processing KML file:', err);
        }
      };
      reader.readAsText(file);
    }
  } catch (error) {
    console.error('Error in onDropMapFile:', error);
  } finally {
    setIsDropActive(false);
  }
}
